import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { getLocale, history, Link } from '@umijs/max';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import CustomerServiceFloat from '@/components/common/CustomerServiceFloat';
import DetailMenuAdapter from '@/components/common/DetailMenuAdapter';
import CustomerService from '@/components/portal/RightContent/CustomerService';
import NoAccessPage from '@/pages/403';
import type { AppProfile } from '@/services/app-info';
import { fetchAppProfile } from '@/services/app-info';
import type { PermissionAction } from '@/services/permission';
import { fetchPermissionResources } from '@/services/permission';
import type { SupportConfigResponse } from '@/services/support-config';
import { fetchSupportInfo } from '@/services/support-config';
import type { BackendMenuItem } from '@/utils/menu-builder';
import { buildMenuTree, toProLayoutMenuData } from '@/utils/menu-builder';
import { isUrlPermitted } from '@/utils/route-access';
import {
  getToken,
  getTokenInfo,
  resolveAvatarUrl,
  setAuthStorage,
} from '@/utils/token';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import {
  authReqHeaderInterceptor,
  authResponseErrorInterceptor,
  authResponseInterceptor,
  startTokenRefreshTimer,
} from './utils/request';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const appUnavailablePath = '/app-unavailable';

/**
 * InitialState 接口定义
 * 包含用户信息、权限、菜单等全局初始状态
 */
export interface InitialState {
  settings?: Partial<LayoutSettings>;
  currentUser?: {
    id: string;
    name: string;
    username: string;
    avatar?: string;
  };
  permissions?: PermissionAction[];
  menus?: BackendMenuItem[];
  isRoot?: boolean;
  /** 多租户支持：应用 ID (Requirement 12.1) */
  app_id?: string;
  /** 多租户支持：租户 ID (Requirement 12.1) */
  tenant_id?: string;
  /** 应用公开信息（名称、Logo、状态） */
  appProfile?: AppProfile | null;
  /** 应用配置项（合并 resource_config + app_config） */
  configs?: Record<string, string>;
  /** 客服联系配置 */
  supportInfo?: SupportConfigResponse | null;
}

/**
 * 获取全局初始状态
 * - 检查 Token 是否存在
 * - 如果有 Token，从 TokenInfo 中获取用户信息
 * - 如果没有 Token 且不在登录页，重定向到登录页
 *
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 */
export async function getInitialState(): Promise<InitialState> {
  const { location } = history;

  // 获取应用公开信息（无需认证）
  const appProfile = await fetchAppProfile();

  // 设置认证存储模式（在获取 token 之前）
  const authStorage =
    (appProfile?.configs?.auth_storage as 'localStorage' | 'cookie') ||
    'localStorage';
  setAuthStorage(authStorage);

  // 动态应用设置：优先使用后端返回的应用名称和 Logo
  const dynamicSettings = {
    ...defaultSettings,
    ...(appProfile?.name ? { title: appProfile.name } : {}),
    ...(appProfile?.logo ? { logo: appProfile.logo } : {}),
  } as Partial<LayoutSettings>;

  // 应用不可用时，重定向到不可用页面（但不要在不可用页面本身重复重定向）
  if (
    appProfile &&
    appProfile.status !== '1' &&
    location.pathname !== appUnavailablePath
  ) {
    history.push(appUnavailablePath);
    return {
      appProfile,
      settings: dynamicSettings,
    };
  }

  // 公开页面路径列表（不需要 Token）
  const publicPaths = [
    loginPath, // '/user/login'
    appUnavailablePath, // '/app-unavailable'
    '/user/register',
    '/tenant/register',
    '/unsubscribe',
  ];

  // 检查是否是公开页面（包括 /agreements/* 动态路由）
  const isPublicPath =
    publicPaths.includes(location.pathname) ||
    location.pathname.startsWith('/agreements/');

  // 如果在公开页面，不需要获取用户信息
  if (isPublicPath) {
    return {
      appProfile,
      configs: appProfile?.configs,
      settings: dynamicSettings,
    };
  }

  // 检查 Token 是否存在
  const token = getToken();
  if (!token) {
    // 没有 Token，根据配置决定跳转行为
    const authMode = appProfile?.configs?.auth_mode;
    const externalLoginUrl = appProfile?.configs?.external_login_url;

    if (authMode === 'external' && externalLoginUrl) {
      // 外部认证模式：跳转到外部登录 URL
      const callbackUrl = encodeURIComponent(window.location.href);
      window.location.href = `${externalLoginUrl}?callback=${callbackUrl}`;
    } else {
      // 内部认证模式：跳转到内置登录页
      history.push(loginPath);
    }
    return {
      appProfile,
      configs: appProfile?.configs,
      settings: dynamicSettings,
    };
  }

  // 从 TokenInfo 中获取用户信息
  const tokenInfo = getTokenInfo();
  const currentUser = tokenInfo
    ? {
        id: tokenInfo.user_id,
        name: tokenInfo.nickname || tokenInfo.username,
        username: tokenInfo.username,
        avatar: resolveAvatarUrl(tokenInfo.picture),
      }
    : undefined;

  // 获取权限资源（菜单、操作权限、超级管理员标识）
  let permissions: PermissionAction[] = [];
  let menus: BackendMenuItem[] = [];
  let isRoot = false;

  try {
    const permissionData = await fetchPermissionResources();
    permissions = permissionData.actions;
    menus = permissionData.menus;
    isRoot = permissionData.root;
  } catch (error) {
    // 权限资源获取失败时使用默认空值
    // 可能是 Token 无效或网络错误，401 会由拦截器处理重定向
    console.error('Failed to fetch permission resources:', error);
  }

  // 多租户上下文：从 TokenInfo 中获取 tenant_id 和 app_id (Requirement 12.1)
  const tenant_id = tokenInfo?.tenant_id;
  const app_id = tokenInfo?.app_id;

  // 启动 Token 定时刷新检查（每 60 秒检查一次，过期前 5 分钟主动刷新）
  startTokenRefreshTimer();

  // 获取客服联系配置
  const supportInfo = await fetchSupportInfo();

  return {
    currentUser,
    permissions,
    menus,
    isRoot,
    tenant_id,
    app_id,
    appProfile,
    configs: appProfile?.configs,
    settings: dynamicSettings,
    supportInfo,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}: {
  initialState?: InitialState;
  setInitialState: (fn: (s?: InitialState) => InitialState) => void;
}) => {
  return {
    // 远程菜单配置：使用 menu.request 从 initialState 加载菜单
    // locale: true 启用国际化，ProLayout 会用 name 作为 formatMessage 的 id
    // params 包含 locale，当语言切换时触发 menu.request 重新执行
    menu: {
      locale: true,
      params: { locale: getLocale() },
      request: async () => {
        const menus = initialState?.menus ?? [];
        const tree = buildMenuTree(menus);
        return toProLayoutMenuData(tree);
      },
    },
    // CDN 详情页替换菜单内容区（保留 ProLayout sider 结构和样式）
    // 支持站点详情和节点详情的侧边栏导航
    menuContentRender: (_props: any, defaultDom: any) => (
      <DetailMenuAdapter defaultDom={defaultDom} />
    ),
    // 处理外部链接菜单项：external_link 为 true 时在新标签页打开
    menuItemRender: (menuItemProps: any, defaultDom: any) => {
      if (menuItemProps.externalLink && menuItemProps.path) {
        return (
          <a
            href={menuItemProps.path}
            target="_blank"
            rel="noopener noreferrer"
          >
            {defaultDom}
          </a>
        );
      }
      if (menuItemProps.path) {
        return <Link to={menuItemProps.path}>{defaultDom}</Link>;
      }
      return defaultDom;
    },
    actionsRender: () => {
      const items: React.ReactNode[] = [];
      const cs = initialState?.supportInfo;
      if (
        cs?.enabled &&
        (cs.display_position === 'header' || cs.display_position === 'both')
      ) {
        items.push(<CustomerService key="cs" />);
      }
      items.push(<Question key="doc" />, <SelectLang key="SelectLang" />);
      return items;
    },
    // 动态标题和 Logo：优先使用后端返回的应用信息
    title:
      initialState?.configs?.logo_display_mode === 'logo_only'
        ? false
        : initialState?.appProfile?.name ||
          initialState?.settings?.title ||
          'Skeleton Admin',
    logo:
      initialState?.configs?.logo_display_mode === 'title_only' ? (
        false
      ) : initialState?.configs?.logo_display_mode === 'logo_only' ? (
        <img
          src={
            initialState?.appProfile?.logo ||
            (initialState?.settings as any)?.logo
          }
          alt="logo"
          style={{
            height: 32,
            width: 'auto',
            maxWidth: 200,
            objectFit: 'contain',
          }}
        />
      ) : (
        initialState?.appProfile?.logo || (initialState?.settings as any)?.logo
      ),
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_: any, avatarChildren: any) => {
        return <AvatarDropdown menu>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 应用不可用时重定向
      if (
        initialState?.appProfile &&
        initialState.appProfile.status !== '1' &&
        location.pathname !== appUnavailablePath
      ) {
        history.push(appUnavailablePath);
        return;
      }
      // 根路径重定向到 Resource 默认首页（无配置时 fallback 到 /welcome）
      if (location.pathname === '/') {
        const homePage = initialState?.configs?.home_page || '/welcome';
        history.push(homePage);
        return;
      }
      // 如果没有登录，根据配置决定跳转行为
      if (
        !initialState?.currentUser &&
        location.pathname !== loginPath &&
        location.pathname !== appUnavailablePath
      ) {
        const authMode = initialState?.configs?.auth_mode;
        const externalLoginUrl = initialState?.configs?.external_login_url;

        if (authMode === 'external' && externalLoginUrl) {
          // 外部认证模式：跳转到外部登录 URL
          const callbackUrl = encodeURIComponent(window.location.href);
          window.location.href = `${externalLoginUrl}?callback=${callbackUrl}`;
        } else {
          // 内部认证模式：跳转到内置登录页
          history.push(loginPath);
        }
        return;
      }
      // 路由权限检查
      if (initialState?.currentUser && !initialState?.isRoot) {
        const publicPaths = [
          loginPath,
          appUnavailablePath,
          '/user/register',
          '/tenant/register',
          '/unsubscribe',
          '/403',
        ];
        const isPublic =
          publicPaths.includes(location.pathname) ||
          location.pathname.startsWith('/agreements/');
        if (!isPublic) {
          const permitted = isUrlPermitted(
            location.pathname,
            initialState.menus || [],
            false,
          );
          if (!permitted) {
            history.push('/403');
          }
        }
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    headerTitleRender:
      initialState?.configs?.logo_display_mode === 'logo_only'
        ? (logo: any) => (
            <a style={{ display: 'flex', alignItems: 'center' }}>{logo}</a>
          )
        : initialState?.configs?.logo_display_mode === 'title_only'
          ? (_logo: any, title: any) => (
              <a style={{ display: 'flex', alignItems: 'center' }}>{title}</a>
            )
          : undefined,
    // 自定义 403 无权限页面
    // 当路由配置了 access 属性且用户权限不满足时，ProLayout 会渲染此组件
    unAccessible: <NoAccessPage />,
    // 增加一个 loading 的状态
    childrenRender: (children: any) => {
      const cs = initialState?.supportInfo;
      return (
        <>
          {children}
          {cs?.enabled &&
            (cs.display_position === 'float' ||
              cs.display_position === 'both') && (
              <CustomerServiceFloat config={cs} />
            )}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
  requestInterceptors: [
    ...(errorConfig.requestInterceptors || []),
    authReqHeaderInterceptor as any,
  ],
  responseInterceptors: [
    ...(errorConfig.responseInterceptors || []),
    [authResponseInterceptor as any, authResponseErrorInterceptor as any],
  ],
};
